import * as React from "react";

import logger from "../../log";

import type { CardSize } from "./cardChoice";
import {
    canPan,
    CENTRE,
    drawCustomCard,
    exportCard,
    type Focus,
    panFocus,
} from "./customPhoto";

/** Wait for a drag to settle before encoding the card to share. */
const EXPORT_DELAY_MS = 150;

interface Exported {
    blob: Blob;
    /** What it was drawn from: a card from before a drag is stale. */
    photo: ImageBitmap;
    overlay: ImageBitmap;
    focus: Focus;
    card: CardSize;
}

/**
 * The "Your photo" card, painted on a canvas the page shows and encoded to
 * a Blob for sharing once the player stops moving the photo.
 *
 * @param photo The player's photo, decoded; null when it isn't shown.
 * @param overlay The server's transparent card, once it matches the toggles.
 * @param card The size of the card being drawn, which the overlay shares.
 */
export function useCustomCard(
    photo: ImageBitmap | null,
    overlay: Blob | null,
    card: CardSize,
) {
    const [canvas, setCanvas] = React.useState<HTMLCanvasElement | null>(null);
    const [decoded, setDecoded] = React.useState<{
        source: Blob;
        bitmap: ImageBitmap;
    } | null>(null);
    const [focus, setFocus] = React.useState<Focus>(CENTRE);
    const [exported, setExported] = React.useState<Exported | null>(null);
    const [failed, setFailed] = React.useState(false);
    const drag = React.useRef<{ id: number; x: number; y: number } | null>(
        null,
    );

    React.useEffect(() => setFocus(CENTRE), [photo]);

    React.useEffect(() => {
        if (!overlay) return undefined;
        let cancelled = false;
        let bitmap: ImageBitmap | null = null;
        setFailed(false);
        createImageBitmap(overlay)
            .then((b) => {
                if (cancelled) {
                    b.close();
                    return;
                }
                bitmap = b;
                setDecoded({ source: overlay, bitmap: b });
            })
            .catch((e) => {
                if (cancelled) return;
                logger.error(`Could not decode the recap card: ${String(e)}`);
                setFailed(true);
            });
        return () => {
            cancelled = true;
            if (!bitmap) return;
            const closing = bitmap;
            // Forget it as well as closing it: the same card comes back when
            // the player returns from Stats only, and a closed bitmap can't
            // be drawn (drawImage throws).
            setDecoded((d) => (d?.bitmap === closing ? null : d));
            closing.close();
        };
    }, [overlay]);

    // A bitmap decoded from an earlier card is closed, so never drawn.
    const overlayBitmap =
        decoded && overlay && decoded.source === overlay
            ? decoded.bitmap
            : null;

    React.useEffect(() => {
        if (!canvas || !photo || !overlayBitmap) return undefined;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            setFailed(true);
            return undefined;
        }
        drawCustomCard(ctx, photo, overlayBitmap, focus, card);
        let cancelled = false;
        const timer = setTimeout(() => {
            exportCard(canvas)
                .then((blob) => {
                    if (cancelled) return;
                    setExported({
                        blob,
                        photo,
                        overlay: overlayBitmap,
                        focus,
                        card,
                    });
                })
                .catch((e) => {
                    if (cancelled) return;
                    logger.error(
                        `Could not encode the recap card: ${String(e)}`,
                    );
                    setFailed(true);
                });
        }, EXPORT_DELAY_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [canvas, photo, overlayBitmap, focus, card]);

    const onPointerDown = React.useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            e.currentTarget.setPointerCapture?.(e.pointerId);
            drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
        },
        [],
    );
    const onPointerMove = React.useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            const last = drag.current;
            if (!photo || !last || last.id !== e.pointerId) return;
            // Screen pixels to card pixels: the canvas is shown scaled down.
            const scale = card.width / e.currentTarget.clientWidth;
            const dx = (e.clientX - last.x) * scale;
            const dy = (e.clientY - last.y) * scale;
            drag.current = { ...last, x: e.clientX, y: e.clientY };
            setFocus((f) => panFocus(photo, f, dx, dy, card));
        },
        [photo, card],
    );
    const onPointerUp = React.useCallback(() => {
        drag.current = null;
    }, []);

    const ready =
        exported !== null &&
        exported.photo === photo &&
        exported.overlay === overlayBitmap &&
        exported.focus === focus &&
        exported.card === card;
    return {
        canvasRef: setCanvas,
        /** The card to share, when it matches what's on screen. */
        blob: ready ? exported.blob : null,
        ready,
        loading: photo !== null && overlay !== null && overlayBitmap === null,
        failed,
        /** Whether a drag would show any more of the photo. */
        movable: photo !== null && canPan(photo, card),
        dragHandlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
        },
    };
}
